import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, InputAdornment, Tooltip, Typography, Zoom } from "@mui/material";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  changePasswordFromOtp,
  sendOtpForEmail,
  verifyOtpForEmail,
} from "../ApiService";
import { passwordRegex } from "../constants/constants";
import isValidEmail from "../constants/utils";
import CustomDialog, {
  CustomDialogContent,
} from "../customComponents/CustomDialog";
import CustomField from "../customComponents/CustomField";
import CustomLink from "../customComponents/CustomLink";
import CustomOtpInput from "../customComponents/CustomOtpInput";
import SecondaryButton from "../customComponents/CustomSecondaryButton";
import { ReactComponent as EmailIconBlack } from "./../images/icons/emailIconBlack.svg";
import { ReactComponent as InfoIcon } from "./../images/icons/infoIcon.svg";
import { ReactComponent as LockIconBlack } from "./../images/icons/lockIconBlack.svg";
import {
  incrementDialogStep,
  onChangeValue,
  resetDialogStore,
} from "./loginSlice";

export default function ForgotPasswordDialog(props) {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [ChangePassLoading, setChangePassLoading] = useState(false);

  const [cooldownTime, setCooldownTime] = useState(0); // State for cooldown timer
  const [isResendDisabled, setIsResendDisabled] = useState(false); // State to disable resend button

  const dialogEmail = useSelector((state) => state.login.dialogEmail);
  const dialogStep = useSelector((state) => state.login.dialogStep);
  const otp = useSelector((state) => state.login.otp);
  const forgotPassword = useSelector((state) => state.login.forgotPassword);
  const confirmForgotPassword = useSelector(
    (state) => state.login.confirmForgotPassword
  );
  const errors = useSelector((state) => state.login.errors);

  const resetCooldown = () => {
    setCooldownTime(0);
    setIsResendDisabled(false);
  };

  const resendMailOTP = async () => {
    if (cooldownTime > 0) {
      toast.info(`Please wait ${cooldownTime} seconds before resending.`);
      return;
    }

    // Assuming sendOtpForPhone is a function that sends the OTP
    const res = await sendOtpForEmail(dialogEmail, "forgot password");

    if (res?.message === "successfully sent an otp") {
      toast.success(res.message);

      startCooldown(); // Start cooldown after successful OTP request
    } else {
      toast.error(res.message);
    }
  };

  const startCooldown = () => {
    setCooldownTime(45); // Set cooldown to 45 seconds
    setIsResendDisabled(true); // Disable resend button

    const countdownInterval = setInterval(() => {
      setCooldownTime((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(countdownInterval);
          setIsResendDisabled(false); // Enable resend button again after cooldown
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000); // Update every second
  };

  const sendOtpForReset = () => {
    let errorsObj = { ...errors };

    if (!dialogEmail) {
      errorsObj = { ...errorsObj, dialogEmail: "Please enter your email id" };
    } else {
      if (!isValidEmail(dialogEmail)) {
        errorsObj = {
          ...errorsObj,
          dialogEmail: "Please enter valid email id",
        };
      } else {
        setLoading(true);
        sendOtpForEmail(dialogEmail, "forgot password")
          .then((res) => {
            if (res.message === "successfully sent an otp") {
              dispatch(incrementDialogStep());
              setLoading(false);
              toast.success("OTP Sent Successfully");
            } else {
              errorsObj = { ...errorsObj, dialogEmail: "Not found" };
              dispatch(
                onChangeValue({ name: "errors", value: { ...errorsObj } })
              );
              setLoading(false);
            }

            return;
          })
          .catch((err) => {
            if (err.response.status === 400) {
              errorsObj = { ...errorsObj, dialogEmail: "Not found" };
              dispatch(
                onChangeValue({ name: "errors", value: { ...errorsObj } })
              );
              return;
            }
            setLoading(false);
          });
      }
    }
    dispatch(onChangeValue({ name: "errors", value: { ...errorsObj } }));
  };

  const onEmailChange = (e) => {
    if (!!e.target.value && !!errors["dialogEmail"]) {
      let errObj = { ...errors };
      delete errObj["dialogEmail"];
      dispatch(onChangeValue({ name: "errors", value: errObj }));
    }

    dispatch(
      onChangeValue({ name: "dialogEmail", value: e.target.value.trim() })
    );
  };

  const verifyOtp = () => {
    let errorsObj = { ...errors };

    if (!otp) {
      toast.error("Please enter OTP");
      return;
    }
    setVerifyLoading(true);
    verifyOtpForEmail(dialogEmail, otp, "forgot password")
      .then((res) => {
        if (res.status === 200) {
          resetCooldown();
          dispatch(incrementDialogStep());
          setVerifyLoading(false);
        } else {
          errorsObj = { ...errorsObj, otp: "Invalid OTP" };
          dispatch(onChangeValue({ name: "errors", value: errorsObj }));
          setVerifyLoading(false);
        }
      })
      .catch((err) => {
        errorsObj = { ...errorsObj, otp: "Invalid OTP" };
        dispatch(onChangeValue({ name: "errors", value: errorsObj }));
        setVerifyLoading(false);
      });
  };

  const onChangeOtp = (value) => {
    if (!!value && !!errors["otp"]) {
      let errObj = { ...errors };
      delete errObj["otp"];
      dispatch(onChangeValue({ name: "errors", value: errObj }));
    }

    dispatch(onChangeValue({ name: "otp", value: value }));
  };

  const getErrorMessage = (msg) => {
    return (
      <Typography
        style={{ color: "#FF7070", fontSize: "16px", paddingRight: "4px" }}
      >
        {msg}
      </Typography>
    );
  };

  const onClickChangePassword = () => {
    let errorsObj = { ...errors };

    if (!forgotPassword) {
      errorsObj = {
        ...errorsObj,
        forgotPassword: "Please enter your password",
      };
    } else {
      let invalidPassword = forgotPassword.match(passwordRegex);
      if (Boolean(invalidPassword)) {
        errorsObj = {
          ...errorsObj,
          forgotPassword: "Please enter a valid password",
        };
      }
    }

    if (!confirmForgotPassword) {
      errorsObj = {
        ...errorsObj,
        confirmForgotPassword: "Please confirm your password",
      };
    }

    if (
      !!forgotPassword &&
      !!confirmForgotPassword &&
      forgotPassword !== confirmForgotPassword
    ) {
      errorsObj = {
        ...errorsObj,
        confirmForgotPassword: "Passwords do not match",
      };
    }
    if (Object.keys(errorsObj).length === 0) {
      setChangePassLoading(true);
      changePasswordFromOtp(dialogEmail, forgotPassword)
        .then((res) => {
          if (res.status === 200) {
            dispatch(incrementDialogStep());
          }
          setChangePassLoading(false);
        })
        .catch((err) => {
          setChangePassLoading(false);
        });
    } else {
      dispatch(onChangeValue({ name: "errors", value: errorsObj }));
    }
  };

  const getDialogContent = () => {
    switch (dialogStep) {
      case 0:
        return (
          <>
            <Typography
              className="text--highlight--lg fw-500"
              style={{
                color: "var(--text-body-title)",
                paddingBottom: "40px",
              }}
            >
              Enter Your Email Id
            </Typography>
            <Box
              pb={3}
              sx={{
                width: "100%",
                maxWidth: "464px",
              }}
            >
              <CustomField
                fullWidth
                secondary
                value={dialogEmail}
                onChange={onEmailChange}
                placeholder="Enter Your Email Id"
                error={Boolean(errors["dialogEmail"])}
                errorMessage={
                  <Box display="flex" alignItems="center">
                    {errors["dialogEmail"] === "Not found" ? (
                      <>
                        <ErrorOutlineIcon
                          sx={{ color: "#FFF178", paddingRight: "8px" }}
                        />
                        <Typography
                          style={{
                            color: "#FFF178",
                            fontSize: "16px",
                            fontWeight: 500,
                            paddingRight: "4px",
                          }}
                        >
                          This email is not registered with us!
                        </Typography>
                        <CustomLink href="/signup">Sign Up</CustomLink>
                      </>
                    ) : (
                      <>
                        <Typography
                          style={{
                            color: "#FF7070",
                            fontSize: "16px",
                            paddingRight: "4px",
                          }}
                        >
                          {errors["dialogEmail"]}
                        </Typography>
                      </>
                    )}
                  </Box>
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIconBlack />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ width: "100%", maxWidth: "464px" }}>
              <SecondaryButton
                sx={{ width: "100%" }}
                onClick={sendOtpForReset}
                isDelDialog={loading}
              >
                Send OTP
              </SecondaryButton>
            </Box>
          </>
        );
      case 1:
        return (
          <>
            <Typography
              style={{
                fontSize: "24px",
                color: "var(--text-body-title)",
                paddingBottom: "8px",
              }}
            >
              Enter your OTP
            </Typography>
            <Typography
              style={{
                fontSize: "16px",
                color: "var(--text-body-title)",
                paddingBottom: "40px",
              }}
            >
              An OTP has been sent on your Email ID
            </Typography>
            <Box
              pb={5}
              sx={{
                width: "100%",
                maxWidth: "464px",
              }}
              className="custom-otp-input-wrapper"
            >
              <CustomOtpInput
                value={otp}
                onChange={onChangeOtp}
                numInputs={6}
                error={errors["otp"]}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  justifyContent: "center",
                  mt: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  className="paragraph--lg"
                  color="var(--text-body-title)"
                >
                  Didn't receive an OTP?
                </Typography>
                <Typography
                  className="paragraph--lg"
                  color="var(--login-highlight-text)"
                  sx={{
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  onClick={resendMailOTP}
                >
                  {isResendDisabled ? `Resend (${cooldownTime}s)` : "Resend"}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ width: "100%", maxWidth: "464px" }}>
              <SecondaryButton
                sx={{ width: "100%" }}
                onClick={verifyOtp}
                isDelDialog={verifyLoading}
              >
                Verify
              </SecondaryButton>
            </Box>
          </>
        );
      case 2:
        return (
          <>
            <Typography
              style={{ fontSize: "32px", color: "#FFF", paddingBottom: "40px" }}
            >
              Reset Password
            </Typography>
            <Box
              pb={2}
              sx={{
                maxWidth: "464px",
                width: "100%",
              }}
            >
              <CustomField
                fullWidth
                secondary
                type={"text"}
                value={forgotPassword}
                error={Boolean(errors["forgotPassword"])}
                errorMessage={getErrorMessage(errors["forgotPassword"])}
                onChange={(e) => onPasswordChange(e, true)}
                heading="Create Password"
                placeholder="Enter password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIconBlack />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="start">
                      <Tooltip
                        TransitionComponent={Zoom}
                        TransitionProps={{ timeout: 300 }}
                        title={
                          <Box display="flex" flexDirection="column" p={1}>
                            <Typography>
                              Password must contain at least: <br />
                              <ul>
                                <li>Eight characters</li>
                                <li>One uppercase letter</li>
                                <li>One lowercase letter</li>
                                <li>One number</li>
                                <li>One special character</li>
                              </ul>
                            </Typography>
                          </Box>
                        }
                      >
                        <InfoIcon style={{ cursor: "default" }} />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box
              pb={4}
              sx={{
                width: "100%",
                maxWidth: "464px",
              }}
            >
              <CustomField
                fullWidth
                secondary
                type={"password"}
                value={confirmForgotPassword}
                error={Boolean(errors["confirmForgotPassword"])}
                errorMessage={getErrorMessage(errors["confirmForgotPassword"])}
                onChange={(e) => onPasswordChange(e, false)}
                heading="Confirm Password"
                placeholder="Confirm password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIconBlack />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ width: "100%", maxWidth: "464px" }}>
              <SecondaryButton
                sx={{ width: "100%" }}
                onClick={onClickChangePassword}
                isDelDialog={ChangePassLoading}
              >
                Change Password
              </SecondaryButton>
            </Box>
          </>
        );
      case 3:
        return (
          <>
            <Typography
              style={{ fontSize: "32px", color: "#FFF", paddingBottom: "40px" }}
            >
              Password Reset Successfully
            </Typography>
            <SecondaryButton
              sx={{
                width: "100%",
                maxWidth: "464px",
              }}
              onClick={onCloseDialog}
            >
              Back to Login
            </SecondaryButton>
          </>
        );
      default:
        return <></>;
    }
  };

  const onCloseDialog = () => {
    props.onClose();
    dispatch(resetDialogStore());
    dispatch(
      onChangeValue({
        name: "errors",
        value: {
          otp: null,
          dialogEmail: null,
        },
      })
    );
  };

  const onPasswordChange = (e, isCreatePassword) => {
    if (Boolean(isCreatePassword)) {
      if (!!e.target.value && !!errors["forgotPassword"]) {
        let errObj = { ...errors };
        delete errObj["forgotPassword"];
        dispatch(onChangeValue({ name: "errors", value: errObj }));
      }

      dispatch(
        onChangeValue({ name: "forgotPassword", value: e.target.value })
      );
    } else {
      if (!!e.target.value && !!errors["confirmForgotPassword"]) {
        let errObj = { ...errors };
        delete errObj["confirmForgotPassword"];
        dispatch(onChangeValue({ name: "errors", value: errObj }));
      }

      dispatch(
        onChangeValue({ name: "confirmForgotPassword", value: e.target.value })
      );
    }
  };

  return (
    <CustomDialog open={props.open} onClose={onCloseDialog} fullWidth>
      <CustomDialogContent>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height={1}
          width={1}
        >
          {getDialogContent()}
        </Box>
      </CustomDialogContent>
    </CustomDialog>
  );
}
